import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, Video, Film, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import SEO from './SEO';
import Footer from './Footer';

const AuthorServices = () => {
  const navigate = useNavigate();

  // Service and Offer Schema for Authors page
  const authorServicesSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": "https://enrolment.bookreviewspecialists.app/book-video-reviews-for-authors#service",
        "name": "Video Book Review Services for Authors",
        "description": "Professional video book review and promotional video services for authors. Get your book noticed with engaging video content including book reviews, trailers, and excerpts.",
        "provider": {
          "@type": "Organization",
          "name": "Video Book Review Specialist",
          "url": "https://enrolment.bookreviewspecialists.app"
        },
        "serviceType": "Video Production",
        "areaServed": "Worldwide",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Video Book Review Packages",
          "itemListElement": [
            {
              "@type": "Offer",
              "name": "Video Book Review - Up to 2 Books",
              "price": "55.00",
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock"
            },
            {
              "@type": "Offer",
              "name": "Video Book Review - Up to 5 Books",
              "price": "180.00",
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock"
            },
            {
              "@type": "Offer",
              "name": "Video Book Review - Up to 9 Books",
              "price": "220.00",
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock"
            },
            {
              "@type": "Offer",
              "name": "Video Promo Book Trailer",
              "price": "60.00",
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock"
            },
            {
              "@type": "Offer",
              "name": "Video Book Excerpts",
              "price": "99.00",
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock"
            }
          ]
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://enrolment.bookreviewspecialists.app/book-video-reviews-for-authors",
        "url": "https://enrolment.bookreviewspecialists.app/book-video-reviews-for-authors",
        "name": "Video Book Reviews for Authors | Professional Video Promotion",
        "description": "Professional video book review services for authors. Boost your book's visibility with engaging video reviews, trailers, and excerpts.",
        "isPartOf": {
          "@id": "https://enrolment.bookreviewspecialists.app/#website"
        }
      }
    ]
  };

  const packages = [
    {
      id: '2_books',
      icon: '📚',
      title: 'Video Book Review',
      subtitle: 'Up to 2 Published Books',
      price: 55,
      features: [
        'Short promotional video review',
        'Introduces your book to readers',
        'Ideal for single titles or two books',
        'Delivered as MP4 video',
        'Includes YouTube link'
      ],
      revolut_link: 'https://checkout.revolut.com/pay/7b393477-a5d3-473b-9615-3a35fcb7236c',
      button_text: 'Book This Review'
    },
    {
      id: '5_books',
      icon: '📚',
      title: 'Video Book Review',
      subtitle: 'Up to 5 Published Books',
      price: 180,
      features: [
        'Promotional video covering multiple books',
        'Perfect for authors with several titles',
        'Delivered as MP4 video',
        'Includes YouTube link'
      ],
      revolut_link: 'https://checkout.revolut.com/pay/626f1bb4-4d38-43ed-84ba-e9a02e5a452e',
      button_text: 'Book This Review'
    },
    {
      id: '9_books',
      icon: '📚',
      title: 'Video Book Review',
      subtitle: 'Up to 9 Published Books',
      price: 220,
      features: [
        'Ideal for authors with large catalogues',
        'Promote multiple books in one video',
        'Delivered as MP4 video',
        'Includes YouTube link'
      ],
      revolut_link: 'https://checkout.revolut.com/pay/6d4c00a1-13af-45af-a934-ef71f11eb708',
      button_text: 'Book This Review',
      popular: true
    },
    {
      id: 'trailer',
      icon: '🎬',
      title: 'Video Promo Book Trailer',
      subtitle: 'Up to 3 Minutes',
      price: 60,
      features: [
        'Short promotional trailer video',
        'Ideal for social media promotion',
        'Delivered as MP4 video',
        'Includes YouTube link'
      ],
      revolut_link: 'https://checkout.revolut.com/pay/6e3fab0f-7d39-4299-863a-9d556caa3bd6',
      button_text: 'Book Trailer Booking'
    },
    {
      id: 'excerpt',
      icon: '📖',
      title: 'Video Book Excerpts',
      subtitle: 'Up to 5 Minutes',
      price: 99,
      features: [
        'Selected passages from your book',
        'Designed to give readers a preview',
        'Delivered as MP4 video',
        'Includes YouTube link'
      ],
      revolut_link: 'https://checkout.revolut.com/pay/fc5f6616-f6de-4fc1-b174-c920bdcb16a1',
      button_text: 'Create Video Excerpt'
    }
  ];

  const handleBooking = (pkg) => {
    // Store selected package in sessionStorage
    sessionStorage.setItem('selected_author_package', JSON.stringify({
      id: pkg.id,
      title: pkg.title,
      subtitle: pkg.subtitle,
      price: pkg.price
    }));
    
    // Redirect to Revolut payment
    window.location.href = pkg.revolut_link;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEO 
        title="Video Book Reviews for Authors | Professional Video Promotion Services"
        description="Promote your book with professional video reviews. Get engaging video book reviews, trailers, and excerpts from £55. Boost your book's visibility and reach new readers."
        canonical="/book-video-reviews-for-authors"
        schema={authorServicesSchema}
      />
      
      {/* Header with Author Login */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-3">
            <a href="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Video Book Review Specialist Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="text-xl font-bold text-slate-800">Video Book Review Specialist</span>
            </a>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-slate-600 hover:text-slate-800 text-sm font-medium">
                Home
              </Link>
              <Link to="/become-a-video-book-review-specialist" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                Become a Specialist
              </Link>
              <Link 
                to="/author-login" 
                className="text-slate-600 hover:text-purple-600 text-sm font-medium"
                data-testid="author-login-link"
              >
                Author Login
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Image */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/6ub4o9m2_AISelect_20260306_162311_Chrome.jpg"
          alt="Get Paid to Review Books Without Reading Them or Being on Camera - Video Book Review Specialist £9.99"
          className="w-full h-auto"
        />
      </div>

      {/* Book Promotion Banner */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/iai2fkbv_Successful%20book%20promotions%20to%20boost%20sales.png"
          alt="Successful Book Promotion is the Key to Boosting Book Sales"
          className="w-full h-auto"
        />
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Promote Your Book with Professional Video Reviews
            </h1>
            <div className="text-xl md:text-2xl space-y-4 text-white/90">
              <p>Writing a book is an incredible achievement.</p>
              <p>But in today's crowded publishing world, <strong>visibility is everything</strong>.</p>
              <p>Readers increasingly discover books through video content on platforms like YouTube, TikTok, Instagram, and author websites.</p>
              <p>Professional video book reviews help introduce your book to new audiences and create engaging promotional content you can use across your marketing channels.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Intro Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xl text-slate-700 leading-relaxed mb-6">
            Our Video Book Review Specialists create short, engaging review videos designed to help authors showcase their books online.
          </p>
          <p className="text-xl text-slate-700 leading-relaxed mb-8">
            Each video can be used as a powerful promotional asset for social media, websites, book launches, and reader engagement.
          </p>
          <p className="text-2xl font-semibold text-blue-600">
            Bring your book to life with video.
          </p>
        </div>
      </div>

      {/* YouTube Video Gallery */}
      <div className="bg-slate-100 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-8 text-center">
            See Our Video Book Reviews in Action
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/G8Cqd4Kxat8"
                title="Video Book Review 1"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/1wfX2MCtNJU"
                title="Video Book Review 2"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/Ris4ZveVhEQ"
                title="Video Book Review 3"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/CPOb9I9vhlA"
                title="Video Book Review 4"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/4GtmJD7-bOI"
                title="Video Book Review 5"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/oIG8HHNZzM4"
                title="Video Book Review 6"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/49tgEZ-RpyM"
                title="Video Book Review 7"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/k2Xn6R6R7PI"
                title="Video Book Review 8"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/Ota82P8TfLo"
                title="Video Book Review 9"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/kCB286GunMI"
                title="Video Book Review 10"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Why Video Reviews Help */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8 text-center">
              Why Video Reviews Help Sell Books
            </h2>
            <p className="text-xl text-slate-700 mb-8 text-center">
              Video reviews provide authors with valuable marketing content that can be used across multiple platforms.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Introduce your book to new readers</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Create engaging social media promotion</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Add credibility through independent commentary</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Improve visibility on YouTube and search engines</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Provide marketing content for your author website</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Support book launches and promotional campaigns</p>
              </Card>
            </div>

            <p className="text-xl text-slate-700 text-center mt-8">
              Many authors struggle to create video content themselves.<br />
              <strong>Our service provides a simple solution.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Packages */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Quiz Banner */}
          <div className="mb-12">
            <a 
              href="https://qualiquiz.app/quiz/what-type-of-video-promotion-does-your-book-need-to-get-more-sales"
              target="_blank"
              rel="noopener noreferrer"
              className="block hover:opacity-90 transition-opacity"
              data-testid="quiz-banner-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/2i7v1yqx_Which%20video%20freviewpromotion%20is%20good%20for%20your%20book.jpg"
                alt="What type of video promotion does your book need? Click to find out!"
                className="w-full rounded-2xl shadow-2xl hover:shadow-3xl transition-shadow"
                data-testid="quiz-banner-image"
              />
            </a>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 text-center">
            Choose Your Video Promotion Package
          </h2>
          <p className="text-xl text-slate-600 mb-12 text-center">
            Promote your book with professionally produced video reviews and promotional content designed to introduce your book to new readers.
          </p>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {packages.map((pkg) => (
              <Card 
                key={pkg.id}
                className={`p-6 relative ${pkg.popular ? 'border-4 border-purple-500 shadow-2xl' : ''}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-purple-600 text-white px-4 py-1 text-sm">
                      Most Popular for Series Authors
                    </Badge>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">{pkg.icon}</div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{pkg.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">{pkg.subtitle}</p>
                  <div className="text-4xl font-bold text-blue-600 mb-2">£{pkg.price}</div>
                </div>

                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => handleBooking(pkg)}
                  className={`w-full ${pkg.popular ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                >
                  {pkg.button_text}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Video Examples Carousel */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
            Promotions, Trailer and Chapter Extraction
          </h2>
          <p className="text-slate-300 text-center mb-10 text-lg">
            See examples of the different video styles we create for authors
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Example 1 - Book Promotion */}
            <div className="bg-slate-700/50 rounded-xl p-4">
              <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg mb-3">
                <iframe
                  src="https://www.youtube.com/embed/N8lMqZKh1Ms"
                  title="Get a copy now - Sucked into the Deception"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-emerald-400 text-sm font-semibold mb-1">Example of Promoting Book</p>
              <p className="text-white text-sm">Get a copy now - Sucked into the Deception</p>
            </div>

            {/* Example 2 - Short Promo */}
            <div className="bg-slate-700/50 rounded-xl p-4">
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg mb-3">
                <iframe
                  src="https://www.youtube.com/embed/7cIdDxN5Spo"
                  title="What do I do now? - Sucked into the Deception"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-pink-400 text-sm font-semibold mb-1">Example Short Promo</p>
              <p className="text-white text-sm">What do I do now? - Sucked into the Deception</p>
            </div>

            {/* Example 3 - Chapter Extract */}
            <div className="bg-slate-700/50 rounded-xl p-4">
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg mb-3">
                <iframe
                  src="https://www.youtube.com/embed/3tIKKVRA2hc"
                  title="The ambush by Teslaverse - Sucked into the Deception"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-purple-400 text-sm font-semibold mb-1">Example Chapter Extract Promo</p>
              <p className="text-white text-sm">The ambush by Teslaverse - Sucked into the Deception</p>
            </div>

            {/* Example 4 - Chapter Extract */}
            <div className="bg-slate-700/50 rounded-xl p-4">
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg mb-3">
                <iframe
                  src="https://www.youtube.com/embed/OMN43EWrvQc"
                  title="Channel Tunnel Black hornet Drone ambush - Sucked into the Deception"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-purple-400 text-sm font-semibold mb-1">Example Chapter Extract Promo</p>
              <p className="text-white text-sm">Channel Tunnel Black hornet Drone ambush</p>
            </div>
          </div>
        </div>
      </div>

      {/* Secure Payment Banner */}
      <div className="bg-slate-100 py-8">
        <div className="container mx-auto px-4">
          <a 
            href="https://checkout.revolut.com/payment-link/1e86a896-653c-4fc4-8b27-06df7d7e7a1d"
            target="_blank"
            rel="noopener noreferrer"
            className="block hover:opacity-95 transition-opacity"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/dt3en482_How%20bookreview%20takes%20payment.jpg"
              alt="We Accept Secure Payments - Visa, Mastercard, Revolut, Stripe, Clearpay - 100% Secure Checkout"
              className="w-full max-w-4xl mx-auto h-auto rounded-lg shadow-lg"
            />
          </a>
        </div>
      </div>

      {/* What You Need */}
      <div className="bg-blue-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">
              What You Need Before Booking
            </h2>
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <p className="text-lg text-slate-700 mb-4">To produce your video review, your book must:</p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                  Already be published online with a URL link
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                  Have a click-to-buy purchase link (Amazon or other retailer)
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                  Provide basic book details during booking
                </li>
              </ul>
              <p className="text-slate-700 mt-4">This allows us to accurately present and promote your book.</p>
            </div>
          </div>
        </div>
      </div>

      {/* What You Receive */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">What You Receive</h2>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <p className="text-xl text-slate-700 mb-6">Each package includes:</p>
            <div className="space-y-4">
              <div className="flex items-start">
                <Video className="w-6 h-6 text-purple-600 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-slate-800">Professionally produced video</p>
                </div>
              </div>
              <div className="flex items-start">
                <FileText className="w-6 h-6 text-purple-600 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-slate-800">MP4 video file for your own promotion</p>
                </div>
              </div>
              <div className="flex items-start">
                <Film className="w-6 h-6 text-purple-600 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-slate-800">YouTube video link for sharing and embedding</p>
                </div>
              </div>
            </div>
            <p className="text-lg text-slate-700 mt-6">
              The supplied MP4 video is yours to use for your own promotional activities.
            </p>
          </div>
        </div>
      </div>

      {/* Content Usage */}
      <div className="bg-slate-100 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-800 mb-6 text-center">Content Usage & Promotion</h2>
            
            <Card className="p-8">
              <p className="text-lg text-slate-700 mb-4">
                The MP4 video supplied is owned by the author and may be used freely for promotional purposes.
              </p>
              <p className="text-lg text-slate-700 mb-4">
                However, the designer of the video reserves the right to:
              </p>
              <ul className="space-y-2 mb-4 ml-6">
                <li className="text-slate-700">• upload the video review to YouTube</li>
                <li className="text-slate-700">• independently promote the author's book through that video</li>
              </ul>
              <p className="text-lg text-slate-700">
                This additional exposure may help introduce the book to a wider audience.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Closing CTA */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Give Your Book the Visibility It Deserves
            </h2>
            
            {/* Competition Banner */}
            <a 
              href="https://www.qualiquiz.app/quiz/free-competition-win-a-free-video-book-review-for-your-book--"
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-8 hover:opacity-90 transition-opacity"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/k2axut4g_file_00000000b87471f4bd146001d5cdbea5.png"
                alt="Authors Knowledge Competition - Win Free Video Book Review! Enter Now"
                className="w-full max-w-xl mx-auto rounded-xl shadow-2xl"
              />
            </a>
            
            <p className="text-xl mb-4 text-white/90">
              In a competitive publishing world, books need more than just a listing on an online store.
            </p>
            <p className="text-xl mb-4 text-white/90">
              They need engaging content that captures attention and introduces readers to your story.
            </p>
            <p className="text-xl mb-8 text-white/90">
              Video reviews provide authors with a powerful way to share their work and connect with new audiences.
            </p>
            <p className="text-2xl font-bold mb-8">
              Start promoting your book with professional video reviews today.
            </p>
            <a 
              href="https://qualiquiz.app/quiz/what-type-of-video-promotion-does-your-book-need-to-get-more-sales"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                className="bg-white text-purple-600 hover:bg-slate-100 text-lg px-10 py-6 rounded-xl"
              >
                Free assessment to find out what you need to make more sales
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AuthorServices;
